/**
 * Service Form Component
 * Add/Edit service modal form
 */

import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCurrency } from '../../contexts/CurrencyContext';

export default function ServiceForm({ service, categories, onSubmit, onClose }) {
  const { symbol } = useCurrency();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '60',
    serviceCategoryId: '',
    depositRequired: '',
    color: '#8b5cf6',
    sortOrder: 999,
    bookableOnline: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name || '',
        description: service.description || '',
        price: service.price || '',
        duration: service.duration || '60',
        serviceCategoryId: service.serviceCategoryId || '',
        depositRequired: service.depositRequired || '',
        color: service.color || '#8b5cf6',
        sortOrder: service.sortOrder || 999,
        bookableOnline: service.bookableOnline !== false
      });
    }
  }, [service]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    console.log('[ServiceForm] Form submitted', formData);

    // Validation
    if (!formData.name.trim()) {
      console.log('[ServiceForm] Validation failed: name required');
      setError('Service name is required');
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      console.log('[ServiceForm] Validation failed: invalid price');
      setError('Price must be greater than 0');
      return;
    }
    if (!formData.duration || parseInt(formData.duration) <= 0) {
      console.log('[ServiceForm] Validation failed: invalid duration');
      setError('Duration must be greater than 0');
      return;
    }

    console.log('[ServiceForm] Validation passed, calling onSubmit');
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        serviceCategoryId: formData.serviceCategoryId || null,
        duration: parseInt(formData.duration),
        price: parseFloat(formData.price),
        depositRequired: formData.depositRequired ? parseFloat(formData.depositRequired) : null,
        color: formData.color || '#8b5cf6',
        sortOrder: formData.sortOrder || 999,
        bookableOnline: formData.bookableOnline !== false
      };
      console.log('[ServiceForm] Payload:', payload);
      await onSubmit(payload);
      console.log('[ServiceForm] Save successful, closing form');
      onClose();
    } catch (err) {
      console.error('[ServiceForm] Save failed:', err);
      console.error('[ServiceForm] Error response:', err.response);
      setError(err.response?.data?.error || err.message || 'Failed to save service');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {service ? 'Edit Service' : 'Add Service'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Swedish Massage"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Brief description of the service"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price ({symbol}) *
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (min) *
              </label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                min="1"
                step="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              name="serviceCategoryId"
              value={formData.serviceCategoryId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">No Category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="bookableOnline"
              id="bookableOnline"
              checked={formData.bookableOnline}
              onChange={handleChange}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="bookableOnline" className="ml-2 text-sm text-gray-700">
              Service is active and bookable online
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : (service ? 'Update Service' : 'Add Service')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
